// GET /api/accounting/summary, /api/accounting/payouts, /api/accounting/payouts/:id
//
// Produced by parkify-pay/api/src/accounting/index.ts, read by the Accounting
// screen, imitated by the demo fixtures in ops/src/demo/data/finance.ts.
//
// Money is always integer cents in a `…Cents` field. Fields added after the
// original shape are optional, because the console deploys separately from the
// Worker and must render against an older API.

/** One day (or month, per `granularity`) of the summary's time series. */
export interface AccountingDailyRow {
  date: string
  revenueCents: number
  sessionsStarted: number
  /** TOTAL contra-revenue: refunds AND chargebacks. */
  refundsCents: number
  /** The disputed subset of `refundsCents`. */
  chargebacksCents?: number
  refundCount: number
  netCents: number
}

export interface AccountingZoneOption {
  zoneKey: string
  label: string
}

export interface AccountingTotals {
  revenueCents: number
  refundsCents: number
  netCents: number
  /** The disputed subset of `refundsCents`. */
  chargebacksCents?: number
  sessionsStarted: number
  refundCount: number
  avgSessionCents: number
}

export interface AccountingZoneRow {
  zoneId: string
  /** Added server-side so the UI shows the lot name, not the raw `zone_…` id. */
  zoneName?: string
  revenueCents: number
  sessions: number
  refundsCents?: number
  chargebacksCents?: number
  /** Refund-netted Parkify commission attributable to this location. */
  parkifyFeeCents?: number
  /** Refund-netted card processing recovered from the operator at this location. */
  processingCents?: number
  /** Refund-netted location tax retained by Parkify to remit to the authority. */
  parkifyHeldTaxCents?: number
  /** Direct-settled rows whose immutable charge-time fee split is incomplete. */
  feeDataUnknownTxnCount?: number
}

/** One legal-entity book in the accounting rollup. `entityId:null` is data
 * that cannot yet be assigned to an LLC and must never be exported by guess. */
export interface AccountingEntityRow {
  clientId: string
  entityId: string | null
  entityName: string
  revenueCents: number
  sessions: number
  refundsCents: number
  chargebacksCents: number
  netCents: number
}

export interface AccountingEntityOption {
  entityKey: string
  clientId: string
  entityId: string | null
  label: string
  assignment: 'entity' | 'unassigned' | 'internal'
}

/** Revenue mix by payment channel (raw key → friendly label via `channelLabel`). */
export interface AccountingChannelRow {
  channel: string
  revenueCents: number
  sessions: number
  refundsCents?: number
}

/**
 * The client's statement in the owner-ruled vocabulary (docs/OWNER-DECISIONS.md,
 * 2026-10-06 "One money vocabulary"). `youKeepCents` is the CLIENT PAYOUT:
 * net sales − Parkify fee − card processing the client bears − tax held.
 * `parkifyFeeCents` is the Parkify fee (rowNetTake), not the Parkify take.
 */
export interface AccountingTakeRate {
  parkifyFeeCents: number
  processingRecoveredCents?: number
  /**
   * Location tax Parkify collected inside the application fee to remit itself,
   * refund-netted. Not Parkify's fee and not the operator's payout. Absent
   * unless a charge in the window carried Parkify-remitted tax.
   */
  parkifyHeldTaxCents?: number
  /**
   * Parkify fee write-off (clamped): the fee Stripe's application-fee clamp kept
   * Parkify from collecting, refund-netted in proportion to the sale
   * (docs/OWNER-DECISIONS.md, 2026-10-07). A MEMO explaining a low fee:
   * `parkifyFeeCents` is already net of it, so it is not subtracted from
   * `youKeepCents` or anything else. Absent when zero.
   */
  feeWriteoffCents?: number
  youKeepCents: number
  takeRatePctBps: number
  takeTxnCount: number
  coverageTxnPct: number
  /**
   * External direct-settled charges whose saved base/admin/service split is
   * incomplete or does not close to the captured amount. Their fee is unknown,
   * never silently reported as zero. Optional for compatibility with older APIs.
   */
  feeDataUnknownTxnCount?: number
  feeDataUnknownGrossCents?: number
}

/** Owner statement grouped by fee model. Net-based, so columns sum to `takeRate`. */
export interface AccountingFeeModelRow {
  feeModel: string
  processingAbsorbedBy: string
  grossCents: number
  refundsCents: number
  netCents: number
  yourPayoutCents: number
  parkifyFeeCents: number
  processingRecoveredCents: number
  /** See AccountingTakeRate.parkifyHeldTaxCents. Absent when zero. */
  parkifyHeldTaxCents?: number
  txnCount: number
  takeTxnCount: number
  /** See AccountingTakeRate.feeDataUnknownTxnCount. */
  feeDataUnknownTxnCount?: number
}

/**
 * Location tax collected in the window, per taxing authority (parkify-demo#107).
 * Present on a response ONLY when some charge in the window carried tax, which
 * is never today: no location is configured with a tax until the CPA answers
 * fee taxability, who remits, and merchant of record. The parker sees one "Tax"
 * line; this is the split behind it that each authority is filed on.
 *
 * `reversedCents` is the tax reversed by refunds and lost disputes of those
 * charges (a positive figure), computed through the same function as the
 * charge, and booked against the charge's own period.
 */
export interface AccountingTaxAuthorityRow {
  authority: string
  taxId: string
  name: string
  rateBps: number
  /** Whether the service fee is inside this tax's base. */
  feeTaxable: boolean
  /**
   * Who files and pays this authority. `'parkify'`: the tax settled inside the
   * application fee and sits in Parkify's balance as a liability. `'operator'`:
   * it travelled in the operator's transfer. A change of remitter mid-window is
   * two rows, not one.
   */
  remitter: 'parkify' | 'operator'
  collectedCents: number
  reversedCents: number
  netCents: number
  chargeCount: number
}

export interface AccountingSummaryResponse {
  range: { from: string; to: string }
  totals: AccountingTotals
  byDay: AccountingDailyRow[]
  byZone: AccountingZoneRow[]
  /** Legal-entity (LLC) books. Includes an explicit unassigned bucket. */
  byEntity?: AccountingEntityRow[]
  byChannel?: AccountingChannelRow[]
  takeRate?: AccountingTakeRate
  byFeeModel?: AccountingFeeModelRow[]
  /** Reporting memorandum only: never included again in statement totals,
   * exports, journals or payment execution. Source terms are reviewed separately. */
  revenueShareAllocations?: AccountingRevenueShareAllocation[]
  /** Per-authority location tax. Absent unless a charge in the window carried tax. */
  taxByAuthority?: AccountingTaxAuthorityRow[]
  /**
   * Imported pre-cutover Mitte history (`channel = 'mitte_import'`) in the
   * window. NOT in totals, takeRate, byFeeModel or any breakdown: Parkify
   * neither collected that money nor paid it out (Mitte did), so it cannot be
   * gross sales or client payout on this statement. A memo line only.
   * Optional: older API.
   */
  importedHistory?: { revenueCents: number; sessions: number }
  /**
   * Parkify-funded validation waivers on charges in the window
   * (transaction_charges.parkify_waiver_cents, migration 0070): the under-50¢
   * remainder a validation leaves, and the grant of a free same-code repeat.
   * A MEMO of Parkify's cost: never inside totals, takeRate, byFeeModel or any
   * breakdown, because no one paid it and no one is paid it. Not refund-netted.
   * Charges before migration 0070 read 0 (forward only). Absent when zero.
   */
  parkifyWaivers?: { cents: number; chargeCount: number }
  /** Date provenance for contra-revenue in this statement window. */
  contraTiming?: {
    providerDatedCents: number
    observedDatedCents: number
    legacyUndatedCents: number
    eventCount: number
  }
  /** Source completeness. A capped or unavailable ledger must never look like
   * a clean $0 period to an export consumer. */
  ledgerQuality?: AccountingLedgerQuality
  /** Provider-neutral guard for any accounting export. A polished file must
   * never be generated from mixed legal entities, an open month, or incomplete
   * source data. Viewing the totals does not require `ready`. */
  exportReadiness?: AccountingExportReadiness
  basis?: 'cash' | 'accrual'
  granularity?: 'day' | 'month'
  filters?: {
    zones: string[]
    productType: string | null
    paymentStatus: string | null
    entityId?: string | null
    unassignedEntityOnly?: boolean
    /** Whole-client consolidated report across every LLC (and unassigned activity). */
    portfolio?: boolean
  }
  availableFilters?: {
    zones: AccountingZoneOption[]
    productTypes: string[]
    paymentStatuses: string[]
    entities?: AccountingEntityOption[]
  }
}

export interface AccountingRevenueShareAllocation {
  zoneId: string
  zoneName: string
  clientId: string
  clientName: string
  clientShareBps: number
  parkifyShareBps: number
  effectivePaidAt: string
  source: string
  status: 'calculated' | 'unavailable'
  problem?: string
  /** Null when the selected source cannot support the calculation. */
  grossCents: number | null
  refundsCents: number | null
  netSalesCents: number | null
  serviceFeeCents: number | null
  shareBasisCents: number | null
  parkifyShareCents: number | null
  clientAllocationCents: number | null
  bankRemittance: 'unverified'
}

/** One day or month in Parkify's internal platform take-rate analytics. */
export interface PlatformTakeRatePoint {
  date: string
  /** Gross payment volume, in integer cents. */
  grossTpvCents: number
  /** Parkify's refund-netted platform fee, in integer cents. */
  netTakeCents: number
  /** netTake ÷ grossTpv, expressed in basis points (divide by 100 for percent). */
  takeRatePctBps: number
}

export interface PlatformTakeRateClientRow {
  clientId: string
  clientName: string
  grossTpvCents: number
  /** Parkify's refund-netted platform fee, in integer cents. */
  netTakeCents: number
  takeRatePctBps: number
  txnCount: number
}

export interface PlatformTakeRateEntityRow {
  clientId: string
  entityId: string
  entityName: string
  grossTpvCents: number
  netTakeCents: number
  takeRatePctBps: number
  txnCount: number
}

export interface PlatformTakeRateZoneRow {
  zoneId: string
  zoneName: string
  clientId: string
  clientName: string
  /** Settlement observed on rows in this window; `none` means only zero-value sessions. */
  settlement: 'connect' | 'direct' | 'mixed' | 'none'
  grossTpvCents: number
  /** Parkify's refund-netted fee, distinct from the settled balance below. */
  netTakeCents: number
  processingRecoveredCents: number
  /** Refund-netted balance retained by Parkify: Connect fee or full direct charge. */
  settledToParkifyCents: number
  takeRatePctBps: number
  txnCount: number
}

export interface PlatformTakeRateModelRow {
  /** A/B/C/D fee model, or `legacy` for unmodeled rows. */
  feeModel: string
  /** Who absorbs processing cost; `—` means not applicable. */
  processingAbsorbedBy: string
  /** Refund-netted split: owner transfer + Parkify take + Stripe fee. */
  driverTotalCents: number
  ownerTransferCents: number
  parkifyTakeCents: number
  stripeFeeCents: number
  txnCount: number
  feeCoveragePct: number
  reconciledOk: boolean
  /** Stripe cost above Parkify's gross take across loss-making rows, in integer cents. */
  /** Optional for compatibility with console builds served against older Workers. */
  negativeMarginCents?: number
  /** Count of rows contributing to `negativeMarginCents`. */
  negativeMarginTxns?: number
  /**
   * Location tax Parkify holds to remit, refund-netted: inside the application
   * fee, never in `parkifyTakeCents`. The identity is then owner + take + Stripe
   * + this = driver. Absent unless a row in the bucket carried Parkify-remitted tax.
   */
  parkifyHeldTaxCents?: number
}

/** GET /api/kpi/take-rate. This reports Parkify's internal platform revenue. */
export interface PlatformTakeRateResponse {
  range: { from: string; to: string }
  basis: 'cash' | 'accrual'
  granularity: 'day' | 'month'
  totals: {
    /** Gross payment volume processed. */
    grossTpvCents: number
    /** Refunds and lost-dispute clawbacks. */
    refundsCents: number
    /** `grossTpvCents - refundsCents`. */
    netTpvCents: number
    /** Refund-netted platform fee retained by Parkify. */
    netTakeCents: number
    /** Parkify's own Stripe processing cost: the ACTUAL fee where captured, net of
     *  operator-funded processing; estimated only for rows still awaiting capture
     *  (see feeCoveragePct). Not a payout reconciliation. Name kept for compatibility. */
    stripeCostEstCents: number
    /** `netTakeCents - stripeCostEstCents`. */
    netAfterStripeCents: number
    /** `netTakeCents ÷ grossTpvCents`, in basis points. */
    takeRatePctBps: number
    txnCount: number
    /** Transactions with a recorded Connect fee or a complete direct-settlement fee split. */
    takeTxnCount: number
    /** `takeTxnCount ÷ txnCount`, as a percentage from 0 to 100. */
    coverageTxnPct: number
    /** Refund-netted amount transferred to operators. */
    ownerTransferCents: number
    /** Residual after owner transfer and Stripe fees. */
    parkifyNetCents: number
    /** Actual Stripe fee where captured, otherwise an estimate. */
    stripeFeeCents: number
    /** Share of charged transactions with actual Stripe fees rather than estimates. */
    feeCoveragePct: number
    /** Location tax Parkify holds to remit, excluded from take. Absent when zero. */
    parkifyHeldTaxCents?: number
  }
  byPeriod: PlatformTakeRatePoint[]
  byClient: PlatformTakeRateClientRow[]
  /** Added after launch; optional while the console may be newer than the Worker. */
  byEntity?: PlatformTakeRateEntityRow[]
  byZone: PlatformTakeRateZoneRow[]
  byFeeModel: PlatformTakeRateModelRow[]
  /** Liveness of the actual-Stripe-fee catch-up (parkify-pay cron/stripe-fee-backfill.ts),
   *  portfolio-wide rather than per window. null when it has never run (or not in
   *  7 days). Optional while the console may be newer than the Worker. */
  feeCapture?: PlatformFeeCaptureStatus | null
  generatedAt: string
}

/** The actual-Stripe-fee catch-up's last run. */
export interface PlatformFeeCaptureStatus {
  /** When the catch-up last ran (ISO). Stale = the backlog figure is stale too. */
  lastRunAt: string
  /** Charged rows, all time, still reporting on the estimate. null = the count failed. */
  remainingUnfilled: number | null
  /** Rows the last run filled with Stripe's actual fee. */
  capturedLastRun: number
}

export type AccountingSourceBlockerCode =
  | 'entity_required'
  | 'incomplete_scope'
  | 'ledger_unavailable'
  | 'ledger_read_failed'
  | 'source_row_cap'
  | 'entity_inactive'
  | 'unassigned_movements'
  | 'legacy_undated_contra'
  | 'observed_contra_date'
  | 'direct_fee_breakdown_unknown'
  /** The range includes the current America/New_York month or a later day. */
  | 'open_period'

export interface AccountingSourceBlocker {
  code: AccountingSourceBlockerCode
  message: string
  count?: number
  amountCents?: number
  sourceIds?: string[]
}

export interface AccountingExportReadiness {
  /** True only for one active legal entity, or one whole client portfolio,
   * with complete, provider-dated data. */
  ready: boolean
  entity: { clientId: string; entityId: string; entityName: string } | null
  /** Set instead of `entity` for a consolidated portfolio report: every LLC of
   * one client plus its unassigned activity, never attributed to an LLC. It is
   * a management report, not any single LLC's legal book. */
  portfolio?: { clientId: string; clientName: string } | null
  blockers: AccountingSourceBlocker[]
}

// ─── Journal-ready accounting contract ─────────────────────────────────────

/** Semantic accounts are stable API concepts. A QuickBooks connection maps
 * every one to one concrete, active chart-of-accounts entry in one company. */
export const ACCOUNTING_JOURNAL_ACCOUNTS = [
  'payment_processor_clearing',
  'parking_revenue',
  'refunds_and_allowances',
  'chargeback_losses',
  'parkify_fees',
  'card_processing_fees',
] as const

export type AccountingJournalAccount = typeof ACCOUNTING_JOURNAL_ACCOUNTS[number]

/** QuickBooks Online Account.AccountType values. */
export type QuickBooksAccountType =
  | 'Bank'
  | 'Accounts Receivable'
  | 'Other Current Asset'
  | 'Fixed Asset'
  | 'Other Asset'
  | 'Accounts Payable'
  | 'Credit Card'
  | 'Other Current Liability'
  | 'Long Term Liability'
  | 'Equity'
  | 'Income'
  | 'Cost of Goods Sold'
  | 'Expense'
  | 'Other Income'
  | 'Other Expense'

export type QuickBooksAccountClassification =
  | 'Asset'
  | 'Equity'
  | 'Expense'
  | 'Liability'
  | 'Revenue'

export interface AccountingMappingRequirement {
  account: AccountingJournalAccount
  classification: QuickBooksAccountClassification
  allowedAccountTypes: QuickBooksAccountType[]
}

/** Runtime source of truth shared by the API validator and a future mapper UI. */
export const ACCOUNTING_QUICKBOOKS_REQUIREMENTS: AccountingMappingRequirement[] = [
  {
    account: 'payment_processor_clearing',
    classification: 'Asset',
    allowedAccountTypes: ['Bank', 'Other Current Asset'],
  },
  {
    account: 'parking_revenue',
    classification: 'Revenue',
    allowedAccountTypes: ['Income'],
  },
  {
    account: 'refunds_and_allowances',
    classification: 'Revenue',
    allowedAccountTypes: ['Income'],
  },
  {
    account: 'chargeback_losses',
    classification: 'Expense',
    allowedAccountTypes: ['Expense', 'Other Expense'],
  },
  {
    account: 'parkify_fees',
    classification: 'Expense',
    allowedAccountTypes: ['Expense', 'Other Expense'],
  },
  {
    account: 'card_processing_fees',
    classification: 'Expense',
    allowedAccountTypes: ['Expense', 'Other Expense'],
  },
]

/** Provider account snapshot selected for one semantic journal account. The
 * realm id is repeated deliberately so mixed-company payloads fail closed. */
export interface AccountingQuickBooksAccountRef {
  id: string
  realmId: string
  name: string
  fullyQualifiedName?: string
  active: true
  classification: QuickBooksAccountClassification
  accountType: QuickBooksAccountType
  accountSubType?: string
}

export type AccountingQuickBooksAccountMap = Record<
  AccountingJournalAccount,
  AccountingQuickBooksAccountRef
>

export interface AccountingQuickBooksMapping {
  provider: 'quickbooks'
  clientId: string
  entityId: string
  realmId: string
  companyName: string
  accounts: AccountingQuickBooksAccountMap
  /** Only a server-side read from this QuickBooks realm may mark a mapping
   * verified. Admin-submitted account snapshots remain pending. */
  verification: {
    status: 'pending' | 'verified'
    verifiedAt?: string
  }
  /** Monotonic optimistic-concurrency token. */
  version: number
  createdAt: string
  updatedAt: string
  updatedBy: {
    actorId: string
    email: string
    name?: string
  }
}

export type AccountingMappingIssueCode =
  | 'storage_unavailable'
  | 'storage_read_failed'
  | 'mapping_missing'
  | 'mapping_corrupt'
  | 'provider_verification_required'
  | 'invalid_value'
  | 'unexpected_field'
  | 'account_missing'
  | 'unknown_semantic_account'
  | 'duplicate_external_account'
  | 'realm_mismatch'
  | 'inactive_account'
  | 'classification_mismatch'
  | 'account_type_mismatch'

export interface AccountingMappingIssue {
  code: AccountingMappingIssueCode
  message: string
  account?: AccountingJournalAccount
  externalAccountId?: string
  allowedAccountTypes?: QuickBooksAccountType[]
}

export interface AccountingMappingState {
  configured: boolean
  ready: boolean
  provider: 'quickbooks'
  mapping: AccountingQuickBooksMapping | null
  issues: AccountingMappingIssue[]
  requirements: AccountingMappingRequirement[]
}

export interface AccountingMappingResponse extends AccountingMappingState {
  entity: {
    clientId: string
    entityId: string
    entityName: string
    active: boolean
  }
}

export interface AccountingMappingUpsertRequest {
  /** null creates; an existing positive version updates. Stale writes fail. */
  expectedVersion: number | null
  provider: 'quickbooks'
  realmId: string
  companyName: string
  accounts: AccountingQuickBooksAccountMap
}

/** Safe connection metadata. OAuth tokens are never part of an API contract. */
export interface AccountingQuickBooksConnectionStatusResponse {
  connected: boolean
  provider: 'quickbooks'
  entity: {
    clientId: string
    entityId: string
    entityName: string
    active: boolean
  }
  connection: null | {
    realmId: string
    companyName: string
    scopes: string[]
    connectedAt: string
    connectedBy: { actorId: string; email: string; name?: string }
    updatedAt: string
    lastVerifiedAt: string
  }
}

export interface AccountingQuickBooksConnectResponse {
  provider: 'quickbooks'
  authorizationUrl: string
  stateExpiresAt: string
}

export interface AccountingQuickBooksCompanyResponse {
  provider: 'quickbooks'
  realmId: string
  companyName: string
  legalName?: string
  country?: string
  fiscalYearStartMonth?: string
  fetchedAt: string
}

export interface AccountingQuickBooksAccountsResponse {
  provider: 'quickbooks'
  realmId: string
  companyName: string
  accounts: AccountingQuickBooksAccountRef[]
  fetchedAt: string
}

export interface AccountingJournalLine {
  account: AccountingJournalAccount
  debitCents: number
  creditCents: number
}

export interface AccountingJournalEntry {
  entryId: string
  kind: 'sale' | 'refund' | 'chargeback'
  txnDate: string
  dateQuality: 'ledger' | 'provider' | 'observed'
  clientId: string
  entityId: string
  entityName: string
  sourceId: string
  chargeId: string
  sessionTxnId: string
  zoneId: string
  channel: string
  memo: string
  currency: 'USD'
  lines: AccountingJournalLine[]
  debitCents: number
  creditCents: number
  balanced: boolean
}

export type AccountingJournalBlockerCode =
  | AccountingSourceBlockerCode
  | 'invalid_allocation'
  | 'location_tax_unmapped'

export interface AccountingJournalBlocker {
  code: AccountingJournalBlockerCode
  message: string
  count?: number
  amountCents?: number
  sourceIds?: string[]
}

export interface AccountingJournalResponse {
  range: { from: string; to: string }
  basis: 'cash' | 'accrual'
  entity: { clientId: string; entityId: string; entityName: string; active: boolean }
  /** Source ledger and journal allocation are safe. */
  ready: boolean
  /** A connector must refuse to post unless source data AND account mapping
   * are both ready, and the range ends before the current America/New_York
   * month. An open month can still be previewed. */
  exportReady: boolean
  mapping: AccountingMappingState
  blockers: AccountingJournalBlocker[]
  entries: AccountingJournalEntry[]
  totals: {
    entryCount: number
    debitCents: number
    creditCents: number
    saleGrossCents: number
    contraCents: number
  }
  generatedAt: string
}

// ─── QuickBooks daily-summary posting ───────────────────────────────────────
// One QuickBooks JournalEntry per zone per day, one Debit and one Credit line
// per semantic account, each carrying the zone's Class (and Location when the
// company tracks Locations). See parkify-pay/docs/QUICKBOOKS-ACCOUNTING-CONNECTION.md.

/** One active QuickBooks Class, read live from the connected realm. */
export interface AccountingQuickBooksClassRef {
  id: string
  realmId: string
  name: string
  fullyQualifiedName?: string
  active: true
}

/** One active QuickBooks Location (the API calls it Department). */
export interface AccountingQuickBooksLocationRef {
  id: string
  realmId: string
  name: string
  fullyQualifiedName?: string
  active: true
}

export interface AccountingQuickBooksClassesResponse {
  provider: 'quickbooks'
  realmId: string
  companyName: string
  classes: AccountingQuickBooksClassRef[]
  /** The company's TrackDepartments preference. When true every zone needs a
   * Location before it can post; when false no zone may carry one. */
  locationTracking: boolean
  /** Active Locations; empty when the company does not track them. */
  locations: AccountingQuickBooksLocationRef[]
  fetchedAt: string
}

/** A zone's stored Class, verified live in `realmId` when it was saved. */
export interface AccountingQuickBooksClassAssignment {
  classId: string
  className: string
  /** The zone's Location, verified live with the Class. Absent when none is mapped. */
  locationId?: string
  locationName?: string
  realmId: string
  verifiedAt: string
  updatedAt: string
  updatedBy: { actorId: string; email: string }
}

export interface AccountingQuickBooksClassMapZone {
  zoneId: string
  /** Customer-facing code (state + zone number, e.g. CA1320) or the zone name. */
  zoneCode: string
  zoneName: string
  /** False when the zone is mapped here but no longer routes to this entity. */
  routedToEntity: boolean
  assignment: AccountingQuickBooksClassAssignment | null
  /** Exactly one active Class whose name equals the zone code or name,
   * ignoring case and spaces. Never applied without an explicit PUT. */
  suggestion?: { classId: string; className: string }
  /** Same rule as `suggestion`, over active Locations. */
  locationSuggestion?: { locationId: string; locationName: string }
}

export interface AccountingQuickBooksClassMapResponse {
  provider: 'quickbooks'
  entity: { clientId: string; entityId: string; entityName: string; active: boolean }
  /** Realm of the entity's book mapping, or null when none exists yet. */
  realmId: string | null
  zones: AccountingQuickBooksClassMapZone[]
  /** Set when live Class discovery for suggestions failed; the map is still exact. */
  suggestionsUnavailable?: string
}

/** Replace the Class (and Location) for each listed zone. `classId: null`
 * removes the row; `locationId: null` saves the Class with no Location, and an
 * omitted `locationId` keeps the zone's stored Location (re-verified live). */
export interface AccountingQuickBooksClassMapUpsertRequest {
  assignments: Array<{ zoneId: string; classId: string | null; locationId?: string | null }>
}

export interface AccountingQuickBooksPostRequest {
  from: string
  to: string
  /** Defaults to true. A real post needs `dryRun: false` AND the server flag. */
  dryRun?: boolean
}

export type AccountingQuickBooksPostingRowStatus = 'pending' | 'posted' | 'failed'

export type AccountingQuickBooksPostDayOutcome =
  /** No journal activity that day; nothing to post. */
  | 'empty'
  /** Dry run: every local gate passed and the payload below would be sent. */
  | 'ready'
  /** Created in QuickBooks by this request (or adopted by DocNumber). */
  | 'posted'
  /** Already posted with identical content; nothing sent. */
  | 'already_posted'
  | 'blocked'
  /** Posted earlier with different content. Never overwritten. */
  | 'drift'
  /** Another writer holds the day's claim. */
  | 'in_progress'
  | 'failed'

export type AccountingQuickBooksPostBlockerCode =
  | 'posting_disabled'
  | 'gate_selftest_failed'
  | 'not_connected'
  | 'mapping_not_ready'
  | 'realm_mismatch'
  | 'journal_unavailable'
  | 'journal_not_export_ready'
  | 'entry_invalid'
  | 'class_missing'
  | 'class_realm_mismatch'
  /** The company tracks Locations and this zone has none mapped. */
  | 'location_missing'
  /** This zone has a Location mapped but the company does not track Locations. */
  | 'location_tracking_disabled'
  /** The entity-day was claimed by the earlier one-entry-per-LLC writer. */
  | 'legacy_entity_post'
  | 'unbalanced'
  | 'too_many_lines'
  | 'provider_verification_failed'
  | 'content_drift'
  | 'doc_number_conflict'
  | 'claim_conflict'
  | 'provider_write_failed'
  | 'halted_after_failure'

export interface AccountingQuickBooksPostBlocker {
  code: AccountingQuickBooksPostBlockerCode
  message: string
  zoneIds?: string[]
  journalBlockers?: AccountingJournalBlocker[]
}

export interface AccountingQuickBooksPostLine {
  account: AccountingJournalAccount
  accountId: string
  accountName: string
  classId: string
  className: string
  locationId?: string
  locationName?: string
  postingType: 'Debit' | 'Credit'
  amountCents: number
  zoneIds: string[]
}

/** One zone's entry for one day. A day with no activity, or one that is
 * blocked before zones are known, is a single row with no zone. */
export interface AccountingQuickBooksPostDay {
  txnDate: string
  zoneId?: string
  /** The zone's customer-facing code (GA1110), or its id when the record is gone. */
  zoneCode?: string
  outcome: AccountingQuickBooksPostDayOutcome
  docNumber: string
  entryCount: number
  debitCents: number
  creditCents: number
  contentHash?: string
  lines?: AccountingQuickBooksPostLine[]
  blockers: AccountingQuickBooksPostBlocker[]
  /** The durable row for this day, when one exists. */
  posting?: {
    status: AccountingQuickBooksPostingRowStatus
    contentHash: string
    attempts: number
    qbJournalEntryId?: string
    postedAt?: string
    lastErrorCode?: string
  }
}

export interface AccountingQuickBooksPostResponse {
  provider: 'quickbooks'
  dryRun: boolean
  postingEnabled: boolean
  entity: { clientId: string; entityId: string; entityName: string; active: boolean }
  realmId: string | null
  range: { from: string; to: string }
  /** Daily summaries are cash basis: they land in the processor clearing
   * account on the day the money moved, which is what reconciles to Stripe. */
  basis: 'cash'
  days: AccountingQuickBooksPostDay[]
  totals: Record<AccountingQuickBooksPostDayOutcome, number>
  generatedAt: string
}

// ─── Payout reconciliation ──────────────────────────────────────────────────
// These mirror Stripe's payout / balance-transaction objects, so they keep
// Stripe's snake_case and its seconds-since-epoch timestamps rather than being
// renamed on the way through. Amounts are Stripe's integer cents.

export type PayoutStatus = 'paid' | 'pending' | 'in_transit' | 'canceled' | 'failed'

/**
 * Stripe's own answer to "can this payout be itemized?".
 *
 *   completed      → `GET /v1/balance_transactions?payout=<id>` works
 *   in_progress    → it will work shortly; the batch isn't sealed yet
 *   not_applicable → Stripe will never itemize it. Only STANDARD AUTOMATIC
 *                    payouts get a reconciliation report, so this covers manual
 *                    payouts, instant payouts, and auto-debits (the negative
 *                    payout Stripe raises to pull a shortfall back out of our
 *                    bank — "Withdrawal to cover a negative balance").
 *
 * Optional because Stripe only added it to the payout object later; treat a
 * missing value as unknown and let the request itself decide.
 * https://docs.stripe.com/api/payouts/object#payout_object-reconciliation_status
 */
export type PayoutReconciliationStatus = 'completed' | 'in_progress' | 'not_applicable'

export interface Payout {
  id: string
  amount: number
  arrival_date: number
  created: number
  currency: string
  status: PayoutStatus
  type: string
  method: string
  description?: string | null
  statement_descriptor?: string | null
  failure_message?: string | null
  /** False for a manually-requested payout. Auto-debits are automatic. */
  automatic?: boolean
  reconciliation_status?: PayoutReconciliationStatus
}

export interface PayoutListResponse {
  data: Payout[]
  has_more: boolean
  /**
   * True when the caller is client-scoped and the platform payout list was
   * withheld — these rows are Parkify's own bank deposits, not the operator's.
   * `data` is empty in that case; the operator's own payouts come from the
   * Connect route instead.
   */
  scoped?: boolean
}

export interface BalanceTxn {
  id: string
  amount: number
  net: number
  fee: number
  currency: string
  type: string
  description?: string | null
  created: number
  available_on: number
  /** Expanded object when the Worker expanded it, otherwise the bare id. */
  source?: string | {
    id?: string
    object?: string
    amount?: number
    metadata?: Record<string, string>
    billing_details?: { email?: string | null; name?: string | null }
    receipt_email?: string | null
    charge?: string | { id?: string } | null
    source_transaction?: string | { id?: string } | null
  }
  /**
   * OURS, not Stripe's — the lot this row's money came from, resolved by the
   * Worker (see attributeZones in payments/payouts.ts). null on the payout's own
   * sweep row, on account-level rows that belong to no lot, and when the parent
   * charge couldn't be resolved. The prefix marks it apart from the raw
   * passthrough fields around it.
   */
  parkifyZoneId?: string | null
  parkifyZoneName?: string | null
}

/**
 * Does the payout's balance-transaction detail add up to the payout amount?
 * `matched` is the assertion; `delta` is what's left over when it doesn't.
 *
 * Every figure here EXCLUDES the payout's own sweep row. Stripe's
 * `?payout=<id>` batch contains it, and counting it made `net` ≈ 0 and `delta`
 * ≈ the whole payout on every payout — a permanently-red banner.
 */
export interface PayoutReconciliation {
  charges: number
  refunds: number
  fees: number
  adjustments: number
  net: number
  payoutAmount: number
  delta: number
  /** The sweep row excluded from `net` (negative on a normal payout). */
  payoutRowNetCents: number
  /**
   * null when there is no assertion to make, rather than a failed one:
   *   - tenant-scoped, where delta is "the rest of the payout that isn't yours"
   *   - no itemization, where every figure above is 0 because Stripe wouldn't
   *     return the batch (see `PayoutItemization`)
   *   - a truncated batch (`itemizationTruncated`), where delta is the rows we
   *     didn't read plus any real mismatch, so it cannot show whether money is
   *     missing either way
   * Rendering null as a red "Mismatch" is the bug this guards.
   */
  matched: boolean | null
}

/**
 * One lot's contribution to a payout. `zoneId: null` is the unattributed
 * bucket — account-level rows that belong to no lot (Stripe monthly billing,
 * Radar, top-ups) plus anything whose parent charge couldn't be resolved.
 */
export interface PayoutZoneRow {
  zoneId: string | null
  zoneName: string
  /** Σ net — what this lot actually contributed to the money that was paid out. */
  netCents: number
  /** Σ amount on this lot's charge/payment rows (gross that entered the balance). */
  grossCents: number
  /** Σ Stripe processing fee on this lot's rows. */
  feeCents: number
  txnCount: number
}

/**
 * How the zone table's parent-charge lookups went.
 *
 * The lookup pass is fail-soft — one unresolvable charge must not blank an
 * otherwise-correct drilldown — so a failure is indistinguishable from a clean
 * run unless it is counted. On 2026-08-12 that pass fired 60 straight 404s
 * against connected-account ids, misfiled a lot's earned fee as Unattributed,
 * and the only witness anywhere was Stripe's own API-health dashboard.
 */
export interface ZoneLookupStats {
  /** Reads actually issued — post-dedup and post-cap. */
  attempted: number
  /**
   * Stripe has no such charge. Should be 0: every id we follow is meant to be a
   * platform-side charge, so a non-zero count is our reference resolution being
   * wrong (the connected-account `py_…` case), not a Stripe problem.
   */
  notFound: number
  /** Other non-OK statuses and thrown fetches — usually transient. */
  errored: number
}

/**
 * Whether Stripe gave us the payout's line items at all.
 *
 * Not every payout can be drilled into: Stripe only itemizes standard automatic
 * payouts, and answers `?payout=<id>` on anything else with a 400. That 400 is
 * about the REPORT, not the payout — the payout header, amount, and status are
 * all still good — so it degrades the drilldown instead of failing it.
 */
export interface PayoutItemization {
  /** True when `transactions` is the payout's real, complete batch. */
  available: boolean
  /**
   * not_applicable  → Stripe never itemizes this kind of payout (auto-debit,
   *                   manual, instant). Nothing to retry.
   * in_progress     → the batch isn't sealed yet; it will itemize later.
   * stripe_rejected → Stripe refused the report despite the payout not being
   *                   flagged `not_applicable`. Kept distinct so a NEW refusal
   *                   is visible rather than reading as an expected gap.
   */
  reason?: 'not_applicable' | 'in_progress' | 'stripe_rejected'
  /** Stripe's own words when it refused, for the UI and for triage. */
  stripeMessage?: string | null
}

export interface PayoutDetailResponse {
  payout: Payout
  /** Every row of the payout's batch, walked page by page up to the api's cap. */
  transactions: BalanceTxn[]
  /** Same value as `itemizationTruncated`; kept for SPA builds that read it. */
  has_more: boolean
  /**
   * The batch had more rows than the api's page cap reads, so `transactions`,
   * `byZone` and every reconciliation total cover only part of the payout and
   * `reconciliation.matched` is null. Optional for the same deploy-skew reason
   * as `zoneLookup`: an api that predates it read one page and never set it.
   */
  itemizationTruncated?: boolean
  /** Present when the caller is tenant-scoped — the drilldown shows only their slice. */
  tenantScoped?: boolean
  itemization: PayoutItemization
  /** Which lots funded this payout. Sorted by net desc, unattributed last. */
  byZone: PayoutZoneRow[]
  /** Hit the parent-charge lookup cap, so more rows sit in `unattributed` than
   *  strictly necessary. Surfaced rather than silently under-reporting a lot. */
  zoneLookupTruncated: boolean
  /**
   * Outcome of the lookups we DID issue. `zoneLookupTruncated` covers the rows we
   * never asked about; this covers the ones we asked about and didn't get.
   *
   * Optional only because the api Worker and the ops SPA deploy separately: an
   * SPA built with this field can be served a response from an api that predates
   * it. A missing object degrades to "no warning shown", the same way a missing
   * `zoneLookupTruncated` would. The api always sets it.
   */
  zoneLookup?: ZoneLookupStats
  reconciliation: PayoutReconciliation
}

// ─── Stripe money-flow statement (GET /api/accounting/reconciliation) ───────
//
// The payout-first shapes above answer "does THIS payout add up". These answer
// the bigger question the Accounting screen actually asks: where did every
// dollar go between the driver's card and the two banks (Parkify's and the
// operator's). See parkify-pay/api/src/accounting/reconciliation.ts for why a
// Connect platform running destination charges can't be reconciled off
// platform payouts alone.

/**
 * Platform-account activity for the range, bucketed from Stripe balance
 * transactions. Reads top to bottom as the statement:
 *
 *   gross − stripeFee − refunds − transferredOut ± adjustments = retained
 *   retained − payouts = change in Parkify's Stripe balance
 */
export interface ReconciliationPlatform {
  grossCents: number
  stripeFeeCents: number
  refundsCents: number
  /** Moved to operators' connected accounts (positive = out). */
  transferredOutCents: number
  /**
   * Parkify's cut arriving back as its own balance transaction. Whether a
   * `transfer` carries the full charge (fee returned separately, which is what
   * the live platform does) or the net of it depends on how the charge was
   * built — so this line is what keeps "transferred to operators" honest.
   */
  applicationFeesCents: number
  /**
   * Charge-time halves of the two lines above: outbound `transfer` rows only
   * and `application_fee` rows only (no reversals, no fee refunds). Their
   * difference is the tie-out's Stripe transfers figure. Optional during
   * API/console deployment skew.
   */
  transfersSentCents?: number
  applicationFeesChargedCents?: number
  adjustmentsCents: number
  otherCents: number
  /** Σ balance-txn net excluding payouts — what Parkify kept. */
  retainedCents: number
  /** Left the balance for Parkify's bank (positive = paid out). */
  payoutsCents: number
  /** Payouts that actually moved money — $0.00 payouts are not counted here. */
  payoutCount: number
  /**
   * Payouts Stripe created for $0.00 — a daily payout schedule firing against a
   * balance it can't touch (typically a minimum-balance hold). They move no
   * money, so the UI hides them and reports the count instead.
   */
  zeroPayoutCount: number
  txnCount: number
  /** True when the range exceeded the page cap and totals are partial. */
  truncated: boolean
  /** Balance RIGHT NOW, not as of the range end. Null if Stripe didn't answer. */
  availableCents: number | null
  pendingCents: number | null
}

/** One payout entity (LLC = one connected account) and where its money is. */
export interface ReconciliationEntity {
  clientId: string
  clientName: string
  entityId: string
  entityName: string
  stripeAccountId: string
  /** Paid out to this operator's bank, filtered on arrival date. $0.00 payouts excluded. */
  payoutsCents: number
  payoutCount: number
  lastPayoutArrival: number | null
  pendingPayoutsCents: number
  availableCents: number | null
  pendingCents: number | null
  /** Set when Stripe rejected the call for this account; the row still renders. */
  error: string | null
}

/** Our D1 ledger over the identical window — the tie-out column. */
export interface ReconciliationLedger {
  grossCents: number
  refundsCents: number
  netCents: number
  parkifyFeeCents: number | null
  ownerTransferCents: number
  processingRecoveredCents: number | null
  /** False when a direct charge/event lacks a trustworthy fee breakdown. */
  feeAllocationComplete?: boolean
  txnCount: number
  /** False when no row in the window carries `owner_transfer_cents` (pre-0023). */
  hasTransferData: boolean
  /**
   * Location tax inside `grossCents`, net of reversals, and its per-authority
   * split. Both absent unless a charge in the window carried tax (never today).
   * Tax is inside the charge Stripe captured, so it is inside gross on both
   * sides of the tie-out; it is shown so the figure filed per authority can be
   * traced to the same rows.
   */
  taxCents?: number
  /**
   * `taxCents` by who holds it. Parkify-remitted tax settled inside the
   * application fee and sits in Parkify's balance as a liability (it is not in
   * `parkifyFeeCents`); operator-remitted tax went out in the transfer. Present
   * exactly when `taxCents` is.
   */
  parkifyHeldTaxCents?: number
  operatorHeldTaxCents?: number
  taxByAuthority?: AccountingTaxAuthorityRow[]
}

export interface AccountingLedgerQuality {
  available: boolean
  readFailed: boolean
  truncated: boolean
  chargeRowCapHit: boolean
  contraEventRowCapHit: boolean
}

/**
 * Stripe vs ledger on the two figures that are apples-to-apples. Refunds are
 * deliberately absent — the ledger books them against the original session's
 * day, Stripe against the issue date, so a delta there is expected, not a bug.
 */
/**
 * Stripe vs our ledger, in bank-reconciliation form.
 *
 * The two sides cannot be cut on the same window: Stripe's
 * /v1/balance_transactions filters `created` in UTC and knows nothing about
 * zones, while our ledger reports on each lot's LOCAL calendar day (the
 * operator's period, and what the Summary tab shows). A session paid at
 * 2026-08-01T00:38Z is in the operator's July and Stripe's August — both
 * correct.
 *
 * So the delta is taken against `*LedgerComparableCents`: native Parkify rows
 * re-cut on Stripe's UTC window. Revenue imported from another payment rail and
 * the UTC/local timing boundary are published as separate reconciling items,
 * not errors. Differencing Stripe against the unadjusted local figure instead
 * prints permanent mismatches on books that are exactly right.
 */
export interface ReconciliationTieOut {
  grossStripeCents: number
  /** Our books on the operator's local window — agrees with the Summary tab. */
  grossLedgerCents: number
  /** The same rows re-cut on Stripe's UTC window. What the delta is taken from. */
  grossLedgerComparableCents: number
  /**
   * Gross in the local ledger from history imported from another payment rail.
   * Valid operator revenue, but not a Parkify Stripe charge. Optional during
   * independent API/console deployment skew.
   */
  importedRailCents?: number
  /** How many local-ledger rows make up `importedRailCents`. */
  importedRailRowCount?: number
  /** Up to 5 imported txn_ids, so the adjustment is findable. */
  importedRailTxnIds?: string[]
  /**
   * `(grossLedgerCents − importedRailCents) − grossLedgerComparableCents`.
   * Positive = native rows the operator counts in this period that Stripe
   * booked in the next one.
   */
  boundaryCents: number
  /** How many rows sit in one window but not the other, both directions. */
  boundaryRowCount: number
  /** Up to 5 of those txn_ids, so the row is findable. Truth is the count. */
  boundaryTxnIds: string[]
  /** `grossStripeCents − grossLedgerComparableCents`. Zero when the books tie. */
  grossDeltaCents: number
  /**
   * NET to operators at charge time: transfers sent − application fees charged.
   * Compared with the ledger's owner_transfer_cents (amount − application fee).
   * Not the statement's gross `transferredOutCents`, which also nets reversals.
   */
  transfersStripeCents: number
  transfersLedgerCents: number
  transfersLedgerComparableCents: number
  transfersDeltaCents: number
  /**
   * False when the ledger has no transfer data for the window, so the delta is
   * "we didn't record it", not "we disagree". `matched` ignores the row.
   */
  transfersComparable: boolean
  matched: boolean
}

export interface ReconciliationResponse {
  range: { from: string; to: string }
  currency: string
  /** True for tenant callers: `platform` and `tieOut` are withheld (Parkify P&L). */
  scoped: boolean
  platform: ReconciliationPlatform | null
  platformError: string | null
  entities: ReconciliationEntity[]
  /** Connected accounts past the per-request cap, not fetched. */
  entitiesSkipped: number
  ledger: ReconciliationLedger
  /** Completeness of the D1 side. `tieOut` is withheld unless this is clean. */
  ledgerQuality?: AccountingLedgerQuality
  /**
   * Null when not evaluated: tenant scope, a Stripe error, a capped Stripe read
   * (`platform.truncated`), or an unavailable/capped ledger (`ledgerQuality`).
   */
  tieOut: ReconciliationTieOut | null
}

// ─── Per-charge Connect transfer reconciliation ─────────────────────────────
// parkify-pay/api/src/accounting/transfer_reconciliation.ts. Staff only.

/** Why a charge's transfer disagrees with the ledger. */
export type TransferIssue =
  | 'transfer_missing'          // a destination charge with no Stripe Transfer
  | 'amount_mismatch'           // transfer − application fee ≠ ledger owner_transfer_cents
  | 'destination_mismatch'      // paid a different connected account than the ledger's entity
  | 'refund_not_reversed'       // refunded, but the client's transfer was not reversed
  | 'refund_reversal_mismatch'  // refund reversal ≠ the refund's proportional share
  | 'unexplained_reversal'      // a reversal that is neither a refund nor a dispute recovery
  | 'dispute_unrecovered'       // lost dispute on the ledger, no recovery reversal yet
  | 'reversals_incomplete'      // Stripe's reversal list did not add up to amount_reversed

export interface TransferReconRow {
  stripeChargeId: string
  stripeIntentId: string
  sessionTxnId: string | null
  clientId: string | null
  entityId: string | null
  zoneId: string | null
  chargeAmountCents: number
  chargeCreatedAt: string
  transferId: string | null
  transferDestination: string | null
  expectedDestination: string | null
  /** Gross transfer: Stripe moves the full charge and takes the fee back separately. */
  transferCents: number | null
  applicationFeeCents: number | null
  expectedNetCents: number | null
  actualNetCents: number | null
  reversedRefundCents: number
  reversedDisputeCents: number
  reversedOtherCents: number
  stripeRefundedCents: number
  ledgerDisputedCents: number
  clientKeptCents: number | null
  status: 'ok' | 'mismatch' | 'unverified'
  issues: TransferIssue[]
  firstFlaggedAt: string | null
  checkedAt: string
}

/** The last daily sweep, as the cron stamped it. */
export interface TransferReconRun {
  at: string
  ok: boolean
  scanned: number
  connect: number
  checked: number
  mismatch: number
  unverified: number
  unledgered: number
  truncated: boolean
  /** The detector flagged every known-bad synthetic charge this run. */
  probeBites: boolean
  payoutsItemized: number
  /** "Payout landed" emails sent this run (notifications/payout_landed.ts). */
  payoutEmailsSent?: number
  error?: string
}

export interface TransferReconResponse {
  lastRun: TransferReconRun | null
  counts: { ok: number; mismatch: number; unverified: number }
  payouts: { itemized: number; pending: number; unavailable: number }
  /** The 25 most recent client payouts stored, newest first — each traceable. */
  recentPayouts: ConnectPayoutTraceResponse['payout'][]
  mismatches: TransferReconRow[]
}

export interface ConnectPayoutTraceResponse {
  payout: {
    id: string
    stripeAccountId: string
    clientId: string | null
    amountCents: number
    status: string
    arrivalDate: string | null
    itemsStatus: 'pending' | 'done' | 'unavailable'
  }
  items: Array<{
    balanceTxnId: string
    type: string
    amountCents: number
    netCents: number
    transferId: string | null
    stripeChargeId: string | null
    sessionTxnId: string | null
    zoneId: string | null
    reconStatus: 'ok' | 'mismatch' | 'unverified' | null
  }>
  itemsNetCents: number
  unlinkedCount: number
}

/**
 * GET /api/accounting/payout-statements/:payoutId — a client's statement for one
 * Stripe payout to their bank (accounting/payout_statement.ts). Every figure comes
 * from Stripe's own itemization of the payout on the client's connected account;
 * the ledger only splits the one deduction Stripe made per charge into its named
 * parts. Names follow docs/OWNER-DECISIONS.md "One money vocabulary".
 */
export type PayoutStatementLineKind =
  | 'charge'        // a parker's payment: Gross sales, less the fees on it
  | 'refund'        // money returned to a parker
  | 'chargeback'    // a lost dispute recovered from the client
  | 'fee_returned'  // the fee on a refunded charge coming back to the client
  | 'adjustment'    // any other adjustment Stripe made
  | 'other'         // a balance entry type this statement does not name

/** Why a statement is not complete. Any entry means the page and the CSV say so. */
export type PayoutStatementGap =
  | 'not_paid'                 // the payout has not reached the bank (or failed)
  | 'not_itemized'             // Stripe's itemization has not been read yet
  | 'itemization_unavailable'  // Stripe will not itemize this payout (manual / instant)
  | 'lines_do_not_sum'         // the lines do not add up to the deposit
  | 'unlinked_lines'           // some lines could not be tied to a parking charge

export interface PayoutStatementLine {
  balanceTxnId: string
  kind: PayoutStatementLineKind
  /** Stripe's balance-transaction type, verbatim. */
  stripeType: string
  chargedAt: string | null
  sessionTxnId: string | null
  zoneId: string | null
  zoneName: string | null
  /** What Stripe moved before fees (negative for money going back). */
  grossCents: number
  /** The deduction split into its parts. All four are 0 on a line with no fee. */
  parkifyFeeCents: number
  cardProcessingCents: number
  taxHeldCents: number
  /** The deduction when the ledger cannot split it: "Parkify fee + card processing". */
  combinedFeesCents: number
  /** What this line added to the deposit (Stripe's net). gross − fees === net. */
  netCents: number
}

export interface PayoutStatementTotals {
  grossSalesCents: number
  refundsAndChargebacksCents: number
  parkifyFeeCents: number
  cardProcessingCents: number
  taxHeldCents: number
  combinedFeesCents: number
  /** fee_returned + adjustment + other lines. */
  adjustmentsCents: number
  /** Σ line net. Equals payoutCents on a complete statement. */
  linesNetCents: number
  payoutCents: number
  /** payoutCents − linesNetCents: what the lines do not explain. */
  unexplainedCents: number
}

export interface PayoutStatement {
  payout: {
    id: string
    clientId: string | null
    entityId: string | null
    amountCents: number
    currency: string
    status: string
    arrivalDate: string | null
    createdAt: string | null
  }
  complete: boolean
  gaps: PayoutStatementGap[]
  totals: PayoutStatementTotals
  lines: PayoutStatementLine[]
}
